import React, {lazy, Suspense, useEffect, useState} from 'react';
import {BrowserRouter as Router, Navigate, Route, Routes} from 'react-router-dom';
import Sidebar from './components/Sidebar';
import ProtectedRoute from './components/ProtectedRoute';
import AucunModule from './components/AucunModule';
import {ROUTE_SANS_MODULE, routeAccueil} from './utils/routeAccueil';
import ErrorBoundary from './components/ErrorBoundary';
import 'bootstrap-icons/font/bootstrap-icons.css';
import {ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import {ThemeProvider, useTheme} from './context/ThemeContext';
import {LanguageProvider} from './context/LanguageContext';
import keycloak from './keycloak';
import {ecrireSidebarCompact, EVENEMENT_SIDEBAR_COMPACT, lireSidebarCompact} from './utils/preferenceSidebar';

// Lazy loading de tous les composants de page
const DossierImportList = lazy(() => import('./components/DossierImportList'));
const DossierImportForm = lazy(() => import('./components/DossierImportForm'));
const DossierImportEdit = lazy(() => import('./components/DossierImportEdit'));
const DossierExportList = lazy(() => import('./components/DossierExportList'));
const NominationExport = lazy(() => import('./components/export/NominationExport'));
const OperationExport = lazy(() => import('./components/export/OperationExport'));
const GestionExport = lazy(() => import('./components/GestionExport'));
const RecapImport = lazy(() => import('./components/RecapImport'));
const RecapExport = lazy(() => import('./components/RecapExport'));
const ArmateursGestion = lazy(() => import('./components/ArmateursGestion'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const ApiDiagnostic = lazy(() => import('./components/ApiDiagnostic'));
const ExportTest = lazy(() => import('./components/ExportTest'));
const DirectDownload = lazy(() => import('./components/DirectDownload'));
const ImportGestion = lazy(() => import('./components/ImportGestion'));
const LivraisonGestion = lazy(() => import('./components/LivraisonGestion'));
const RecuperationGestion = lazy(() => import('./components/RecuperationGestion'));
const Detention = lazy(() => import('./components/Detention'));
const ParcGestion = lazy(() => import('./components/ParcGestion'));
const ChauffeurGestion = lazy(() => import('./components/ChauffeurGestion'));
const CamionGestion = lazy(() => import('./components/CamionGestion'));
const RemorqueGestion = lazy(() => import('./components/RemorqueGestion'));
const ClientGestion = lazy(() => import('./components/ClientGestion'));
const TransitaireGestion = lazy(() => import('./components/TransitaireGestion'));
const LivraisonTrackingDemo = lazy(() => import('./components/LivraisonTrackingDemo'));
const LivraisonRecap = lazy(() => import('./components/LivraisonRecap'));
const ChauffeurNotificationsDashboard = lazy(() => import('./components/ChauffeurNotificationsDashboard'));
const UserManagement = lazy(() => import('./components/UserManagement'));
const Parametres = lazy(() => import('./components/Parametres'));
const RecuperationDashboard = lazy(() => import('./components/RecuperationDashboard'));
const RecuperationSimpleWorkflow = lazy(() => import('./components/RecuperationSimpleWorkflow'));
const FlotteMap = lazy(() => import('./components/FlotteMap'));
const Statistiques = lazy(() => import('./components/Statistiques'));
const CarburantGestion = lazy(() => import('./components/CarburantGestion'));
const RapportsCarburant = lazy(() => import('./components/RapportsCarburant'));
const ParametrageCarburant = lazy(() => import('./components/ParametrageCarburant'));
const PannesGestion = lazy(() => import('./components/PannesGestion'));
const ParametragePannes = lazy(() => import('./components/ParametragePannes'));
const DocumentsCamionGestion = lazy(() => import('./components/DocumentsCamionGestion'));
const RapportsLogistique = lazy(() => import('./components/RapportsLogistique'));

// Composant de chargement léger affiché pendant le lazy loading
const PageLoader: React.FC = () => (
    <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        flexDirection: 'column',
        gap: '12px',
        color: '#6b7280'
    }}>
        <div style={{
            width: '36px',
            height: '36px',
            border: '3px solid #e5e7eb',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
        }}/>
        <span style={{fontSize: '14px'}}>Chargement...</span>
    </div>
);

// Composant interne qui utilise les hooks de contexte
function AppContent() {
    const {theme} = useTheme();
    const [user, setUser] = useState<any>(null);
    const [keycloakReady, setKeycloakReady] = useState(false);
    // Initialisation paresseuse depuis la préférence enregistrée : la barre doit apparaître
    // déjà repliée au premier rendu. Un useEffect qui la replierait après coup produirait un
    // saut visible de 250 à 60 px à chaque chargement.
    const [sidebarWidth, setSidebarWidth] = useState(() => lireSidebarCompact() ? 60 : 250);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(lireSidebarCompact);
    const [isMobileView, setIsMobileView] = useState<boolean>(() => {
        return typeof window !== 'undefined' ? window.innerWidth <= 992 : false;
    });
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

    // Le rechargement automatique après un échec de chunk (cf. ErrorBoundary) ne doit
    // bloquer qu'un seul rechargement en boucle — une fois l'app effectivement montée avec
    // succès, on lève le garde-fou pour qu'un futur échec de chunk (plus tard dans la même
    // session) puisse à nouveau déclencher un rechargement automatique.
    useEffect(() => {
        sessionStorage.removeItem('sg_chunk_reload_guard');
    }, []);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 992;
            setIsMobileView(mobile);
            if (!mobile) setIsMobileSidebarOpen(false);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if ((keycloak as any)._initCalled) return;
        (keycloak as any)._initCalled = true;
        keycloak.init({onLoad: 'login-required', pkceMethod: 'S256', checkLoginIframe: false})
            .then((authenticated) => {
                setKeycloakReady(true);
                if (authenticated && keycloak.tokenParsed) {
                    const token = keycloak.tokenParsed as any;
                    const keycloakRoles: string[] = token.realm_access?.roles || [];
                    const knownRoles = ['ADMIN', 'COMMIS', 'LOGISTICIENNE', 'CHAUFFEUR', 'EXPORTATEUR'];
                    const primaryRole = keycloakRoles.find((r: string) => knownRoles.includes(r))
                        || keycloakRoles[0]
                        || '';

                    const mappedUser = {
                        ...token,
                        role: primaryRole,
                        nom: token.family_name || '',
                        prenom: token.given_name || '',
                        nomComplet: token.name || `${token.given_name || ''} ${token.family_name || ''}`.trim() || token.preferred_username || '',
                        email: token.email || '',
                        username: token.preferred_username || token.sub || '',
                        telephone: token.phone_number || token.telephone || '',
                    };

                    setUser(mappedUser);
                }
            })
            .catch((err) => {
                console.error('Erreur initialisation Keycloak:', err);
                setKeycloakReady(true);
            });
    }, []);

    const handleLogout = () => {
        keycloak.logout({redirectUri: window.location.origin});
    };

    const handleSidebarToggle = (isCollapsed: boolean) => {
        setIsSidebarCollapsed(isCollapsed);
        setSidebarWidth(isCollapsed ? 60 : 250);
        // Le chevron écrit la même préférence que la case à cocher, plutôt que de garder un
        // état de session séparé. Sans cela les deux commandes se contrediraient : replier par
        // le chevron laisserait la case décochée dans Paramètres, et le prochain enregistrement
        // depuis cet écran redéplierait la barre sans que l'utilisateur ait rien demandé.
        ecrireSidebarCompact(isCollapsed);
    };

    // Reflète un changement venu de l'écran Paramètres — ou d'un autre onglet, via storage.
    useEffect(() => {
        const appliquer = () => {
            const compact = lireSidebarCompact();
            setIsSidebarCollapsed(compact);
            setSidebarWidth(compact ? 60 : 250);
        };
        window.addEventListener(EVENEMENT_SIDEBAR_COMPACT, appliquer);
        window.addEventListener('storage', appliquer);
        return () => {
            window.removeEventListener(EVENEMENT_SIDEBAR_COMPACT, appliquer);
            window.removeEventListener('storage', appliquer);
        };
    }, []);

    const computedSidebarWidth = isMobileView ? 0 : sidebarWidth;

    if (!keycloakReady) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                flexDirection: 'column',
                gap: '12px',
                color: '#6b7280'
            }}>
                <div style={{
                    width: '36px',
                    height: '36px',
                    border: '3px solid #e5e7eb',
                    borderTopColor: '#3b82f6',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite'
                }}/>
                <span style={{fontSize: '14px'}}>Connexion à Translon...</span>
            </div>
        );
    }

    if (user && user.role === 'CHAUFFEUR') {
        const chauffeurAppUrl = process.env.REACT_APP_CHAUFFEUR_APP_URL || 'http://localhost:3001';
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                flexDirection: 'column',
                gap: '16px',
                textAlign: 'center',
                padding: '24px',
                color: '#374151'
            }}>
                <i className="bi bi-truck" style={{fontSize: '48px', color: '#16a34a'}}></i>
                <h2 style={{margin: 0}}>Accès réservé</h2>
                <p style={{maxWidth: '420px', margin: 0}}>
                    Votre compte est un compte <strong>Chauffeur</strong>. Cette application
                    est réservée aux équipes administratives. Veuillez utiliser
                    l'application <strong>Translon Chauffeur</strong> pour recevoir et gérer vos ordres de livraison.
                </p>
                <div style={{display: 'flex', gap: '12px'}}>
                    <a
                        href={chauffeurAppUrl}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: '#16a34a',
                            color: '#fff',
                            borderRadius: '8px',
                            textDecoration: 'none',
                            fontWeight: 600
                        }}
                    >
                        Ouvrir l'app Chauffeur
                    </a>
                    <button
                        onClick={handleLogout}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: '#e5e7eb',
                            color: '#374151',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: 600,
                            cursor: 'pointer'
                        }}
                    >
                        Se déconnecter
                    </button>
                </div>
            </div>
        );
    }

    const authenticatedLayout = (
        <div className={`App ${theme === 'dark' ? 'dark-theme' : 'light-theme'}`}
             style={{display: 'flex', flexDirection: isMobileView ? 'column' : 'row'}}>
            {/* Overlay backdrop mobile */}
            {isMobileView && isMobileSidebarOpen && (
                <div
                    className="sidebar-overlay active"
                    onClick={() => setIsMobileSidebarOpen(false)}
                />
            )}

            {/* Bouton hamburger mobile */}
            {isMobileView && (
                <button
                    onClick={() => setIsMobileSidebarOpen(o => !o)}
                    style={{
                        position: 'fixed',
                        top: '12px',
                        left: '12px',
                        zIndex: 1050,
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        border: 'none',
                        background: theme === 'dark' ? '#1e3a5f' : '#1e3a5f',
                        color: '#fff',
                        fontSize: '18px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                    }}
                    aria-label="Menu"
                >
                    <i className={`bi ${isMobileSidebarOpen ? 'bi-x-lg' : 'bi-list'}`}/>
                </button>
            )}

            <div className={isMobileView ? `sidebar ${isMobileSidebarOpen ? 'mobile-open' : ''}` : ''}
                 style={isMobileView ? {zIndex: 1040} : {}}>
                <Sidebar
                    user={user}
                    onLogout={handleLogout}
                    onToggle={handleSidebarToggle}
                    isCollapsed={isSidebarCollapsed}
                    isMobileView={isMobileView}
                />
            </div>
            <main
                className="main-content"
                style={{
                    flex: 1,
                    marginLeft: isMobileView ? 0 : `${computedSidebarWidth}px`,
                    minHeight: '100vh',
                    backgroundColor: theme === 'dark' ? '#1a1a2e' : '#f8f9fa',
                    transition: 'margin-left 0.3s ease, background-color 0.3s ease',
                    paddingTop: isMobileView ? '60px' : undefined,
                    overflowX: 'hidden',
                    width: isMobileView ? '100%' : undefined,
                }}
            >
                <ErrorBoundary>
                    <Routes>
                        <Route path="/" element={<Navigate to={routeAccueil(user)} replace/>}/>
                        <Route path={ROUTE_SANS_MODULE} element={<AucunModule/>}/>
                        <Route path="/dashboard" element={
                            <ProtectedRoute user={user}
                                            modules={['MODULE_IMPORT', 'MODULE_EXPORT', 'MODULE_RECAP', 'MODULE_STATS', 'MODULE_ADMIN']}>
                                <Suspense fallback={<PageLoader/>}><Dashboard/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/import/gestion" element={
                            <ProtectedRoute user={user} modules={['MODULE_IMPORT']}>
                                <Suspense fallback={<PageLoader/>}><ImportGestion/></Suspense>
                            </ProtectedRoute>
                        }/>
                        {/* « Consulter un dossier » supprimé le 2026-09-23. Les deux anciennes
                            adresses mènent à la liste plutôt qu à une page blanche : des favoris
                            et des liens déjà partagés y pointent. */}
                        <Route path="/import/quick-form" element={<Navigate to="/dossiers-import/liste" replace/>}/>
                        <Route path="/import/livraisons" element={
                            <ProtectedRoute user={user} modules={['MODULE_IMPORT']}>
                                <Suspense fallback={<PageLoader/>}><LivraisonGestion/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/import/livraison/nouveau" element={
                            <ProtectedRoute user={user} modules={['MODULE_IMPORT']}>
                                <Suspense fallback={<PageLoader/>}><LivraisonGestion/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/import/recuperations" element={
                            <ProtectedRoute user={user} modules={['MODULE_IMPORT']}>
                                <Suspense fallback={<PageLoader/>}><RecuperationGestion/></Suspense>
                            </ProtectedRoute>
                        }/>
                        {/* Détention (2026-10-25) : mêmes accès que les récupérations. */}
                        <Route path="/import/detention" element={
                            <ProtectedRoute user={user} modules={['MODULE_IMPORT']}>
                                <Suspense fallback={<PageLoader/>}><Detention/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/import/recuperation/nouveau" element={
                            <ProtectedRoute user={user} modules={['MODULE_IMPORT']}>
                                <Suspense fallback={<PageLoader/>}><RecuperationGestion/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/import/parcs" element={
                            <ProtectedRoute user={user} modules={['MODULE_PARC']}>
                                <Suspense fallback={<PageLoader/>}><ParcGestion/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/gestion-parc" element={
                            <ProtectedRoute user={user} modules={['MODULE_PARC']}>
                                <Suspense fallback={<PageLoader/>}><ParcGestion/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/gestion-chauffeurs" element={
                            <ProtectedRoute user={user} modules={['MODULE_REGLAGES']}>
                                <Suspense fallback={<PageLoader/>}><ChauffeurGestion/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/gestion-camions" element={
                            <ProtectedRoute user={user} modules={['MODULE_REGLAGES']}>
                                <Suspense fallback={<PageLoader/>}><CamionGestion/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/gestion-remorques" element={
                            <ProtectedRoute user={user} modules={['MODULE_REGLAGES', 'MODULE_ADMIN']}>
                                <Suspense fallback={<PageLoader/>}><RemorqueGestion/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/gestion-clients" element={
                            <ProtectedRoute user={user} modules={['MODULE_REGLAGES']}>
                                <Suspense fallback={<PageLoader/>}><ClientGestion/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/gestion-transitaires" element={
                            <ProtectedRoute user={user} modules={['MODULE_REGLAGES']}>
                                <Suspense fallback={<PageLoader/>}><TransitaireGestion/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/import/tracking-demo" element={
                            <ProtectedRoute user={user} modules={['MODULE_IMPORT']}>
                                <Suspense fallback={<PageLoader/>}><LivraisonTrackingDemo/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/import/livraison-recap" element={
                            <ProtectedRoute user={user} modules={['MODULE_IMPORT']}>
                                <Suspense fallback={<PageLoader/>}><LivraisonRecap/></Suspense>
                            </ProtectedRoute>
                        }/>
                        {/* Notifications de livraison adressées aux chauffeurs : relève du flux
                        Import, pas des référentiels du sous-menu Réglages — d'où MODULE_IMPORT
                        et non MODULE_REGLAGES, après la suppression de MODULE_CHAUFFEURS. */}
                        <Route path="/chauffeur/notifications" element={
                            <ProtectedRoute user={user} modules={['MODULE_IMPORT', 'MODULE_ADMIN']}>
                                <Suspense fallback={<PageLoader/>}>
                                    <ChauffeurNotificationsDashboard
                                        telephoneChauffeur={localStorage.getItem('chauffeurTel') || ''}/>
                                </Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/dossiers-import" element={<Navigate to="/dossiers-import/liste" replace/>}/>
                        <Route path="/dossiers-import/liste" element={
                            <ProtectedRoute user={user} modules={['MODULE_IMPORT']}>
                                <Suspense fallback={<PageLoader/>}><DossierImportList/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/dossiers-import/nouveau" element={
                            <ProtectedRoute user={user} modules={['MODULE_IMPORT']}>
                                <Suspense fallback={<PageLoader/>}><DossierImportForm/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/dossiers-import/modifier/:id" element={
                            <ProtectedRoute user={user} modules={['MODULE_IMPORT']}>
                                <Suspense fallback={<PageLoader/>}><DossierImportEdit/></Suspense>
                            </ProtectedRoute>
                        }/>
                        {/* Export — nouvelles routes */}
                        <Route path="/export/nouveau" element={
                            <ProtectedRoute user={user} modules={['MODULE_EXPORT']}>
                                <Suspense fallback={<PageLoader/>}><NominationExport/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/export/gestion" element={
                            <ProtectedRoute user={user} modules={['MODULE_EXPORT']}>
                                <Suspense fallback={<PageLoader/>}><GestionExport/></Suspense>
                            </ProtectedRoute>
                        }/>
                        {/* Positionnement et relevage, écrans séparés (2026-10-31). « À relever » renvoie au relevage. */}
                        <Route path="/export/relever" element={<Navigate to="/export/relevage" replace/>}/>
                        <Route path="/export/positionnement" element={
                            <ProtectedRoute user={user} modules={['MODULE_EXPORT']}>
                                <Suspense fallback={<PageLoader/>}><OperationExport key="positionnement" etape="positionnement"/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/export/relevage" element={
                            <ProtectedRoute user={user} modules={['MODULE_EXPORT']}>
                                <Suspense fallback={<PageLoader/>}><OperationExport key="relevage" etape="relevage"/></Suspense>
                            </ProtectedRoute>
                        }/>
                        {/* Référentiels */}
                        <Route path="/referentiels/armateurs" element={
                            <ProtectedRoute user={user} modules={['MODULE_REGLAGES', 'MODULE_ADMIN']}>
                                <Suspense fallback={<PageLoader/>}><ArmateursGestion/></Suspense>
                            </ProtectedRoute>
                        }/>
                        {/* Récapitulatifs */}
                        <Route path="/recap/import" element={
                            <ProtectedRoute user={user} modules={['MODULE_IMPORT']}>
                                <Suspense fallback={<PageLoader/>}><RecapImport/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/recap/export" element={
                            <ProtectedRoute user={user} modules={['MODULE_EXPORT']}>
                                <Suspense fallback={<PageLoader/>}><RecapExport/></Suspense>
                            </ProtectedRoute>
                        }/>
                        {/* Anciennes routes conservées pour compatibilité */}
                        <Route path="/dossiers-export" element={
                            <ProtectedRoute user={user} modules={['MODULE_EXPORT']}>
                                <Suspense fallback={<PageLoader/>}><DossierExportList/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/dossiers-export/nouveau" element={
                            <ProtectedRoute user={user} modules={['MODULE_EXPORT']}>
                                <Suspense fallback={<PageLoader/>}><NominationExport/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/dossiers-export/modifier/:id" element={
                            <ProtectedRoute user={user} modules={['MODULE_EXPORT']}>
                                <Suspense fallback={<PageLoader/>}><NominationExport/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/admin/users" element={
                            <ProtectedRoute user={user} modules={['MODULE_ADMIN']}>
                                <Suspense fallback={<PageLoader/>}><UserManagement/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/parametres" element={
                            <Suspense fallback={<PageLoader/>}><Parametres
                                userRole={user?.realm_access?.roles?.[0] || ''}/></Suspense>
                        }/>
                        <Route path="/recuperations-dashboard" element={
                            <ProtectedRoute user={user} modules={['MODULE_IMPORT']}>
                                <Suspense fallback={<PageLoader/>}><RecuperationDashboard/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/recuperations-simple" element={
                            <ProtectedRoute user={user} modules={['MODULE_IMPORT']}>
                                <Suspense fallback={<PageLoader/>}><RecuperationSimpleWorkflow/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/flotte-carte" element={
                            <ProtectedRoute user={user} modules={['MODULE_IMPORT']}>
                                <Suspense fallback={<PageLoader/>}><FlotteMap/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/statistiques" element={
                            <ProtectedRoute user={user} modules={['MODULE_STATS', 'MODULE_ADMIN']}>
                                <Suspense fallback={<PageLoader/>}><Statistiques/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/diagnostic" element={
                            <ProtectedRoute user={user} modules={['MODULE_ADMIN']}>
                                <Suspense fallback={<PageLoader/>}><ApiDiagnostic/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/test-export" element={
                            <ProtectedRoute user={user} modules={['MODULE_ADMIN']}>
                                <Suspense fallback={<PageLoader/>}><ExportTest/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/download" element={
                            <ProtectedRoute user={user} modules={['MODULE_ADMIN']}>
                                <Suspense fallback={<PageLoader/>}><DirectDownload/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/carburant" element={
                            <ProtectedRoute user={user} modules={['MODULE_CARBURANT', 'MODULE_ADMIN']}>
                                <Suspense fallback={<PageLoader/>}><CarburantGestion/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/carburant/rapports" element={
                            <ProtectedRoute user={user} modules={['MODULE_CARBURANT', 'MODULE_ADMIN']}>
                                <Suspense fallback={<PageLoader/>}><RapportsCarburant/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/carburant/parametrage" element={
                            <ProtectedRoute user={user} modules={['MODULE_CARBURANT', 'MODULE_ADMIN']}>
                                <Suspense fallback={<PageLoader/>}><ParametrageCarburant/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/pannes" element={
                            <ProtectedRoute user={user} modules={['MODULE_PANNES', 'MODULE_ADMIN']}>
                                <Suspense fallback={<PageLoader/>}><PannesGestion/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/pannes/parametrage" element={
                            <ProtectedRoute user={user} modules={['MODULE_PANNES', 'MODULE_ADMIN']}>
                                <Suspense fallback={<PageLoader/>}><ParametragePannes/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/documents-camion" element={
                            <ProtectedRoute user={user} modules={['MODULE_DOCUMENTS', 'MODULE_ADMIN']}>
                                <Suspense fallback={<PageLoader/>}><DocumentsCamionGestion/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="/rapports-logistique" element={
                            <ProtectedRoute user={user} modules={['MODULE_STATS', 'MODULE_ADMIN']}>
                                <Suspense fallback={<PageLoader/>}><RapportsLogistique/></Suspense>
                            </ProtectedRoute>
                        }/>
                        <Route path="*" element={
                            <div className="max-w-7xl mx-auto px-4 mt-4">
                                <div className="bg-yellow-100 border border-yellow-200 text-yellow-800 p-4 rounded-md">
                                    <h2 className="text-lg font-bold">Page non trouvée</h2>
                                    <p>La page que vous recherchez n'existe pas.</p>
                                </div>
                            </div>
                        }/>
                    </Routes>
                </ErrorBoundary>
            </main>
        </div>
    );

    return (
        <Router>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme={theme}
            />
            <Routes>
                <Route path="/*" element={
                    user ? authenticatedLayout : <Navigate to="/" replace/>
                }/>
            </Routes>
        </Router>
    );
}

// Composant App principal avec les providers
function App() {
    return (
        <ThemeProvider>
            <LanguageProvider>
                <AppContent/>
            </LanguageProvider>
        </ThemeProvider>
    );
}

export default App;
